import apiClient from './apiClient';

export interface Agency {
  id: number;
  agencyName: string;
  licenceNumber: string;
  agencyEmail: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  contactPerson: string;
  logoUrl?: string;
  status: 'ACTIVE' | 'PENDING' | 'SUSPENDED' | 'INACTIVE';
  verificationStatus: 'PENDING' | 'VERIFIED' | 'REJECTED';
  subscriptionStatus: 'TRIAL' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED';
  subscriptionExpiration?: string;
  balance: number;
  createdAt: string;
  updatedAt: string;
  captchaToken?: string;
}

export interface AgencyFilter {
  status?: string;
  subscriptionStatus?: string;
  verificationStatus?: string;
  search?: string;
  country?: string;
  page?: number;
  pageSize?: number;
}

export interface AgenciesResponse {
  agencies: Agency[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PackFilters {
  name?: string;
  email?: string;
  status?: string;
}

export interface PaginationParams {
  page?: number; 
  size?: number;
  filters?: PackFilters;
}

export const agencyService = {
  /**
   * Récupère toutes les agences avec filtres optionnels
   */
  getAll: (params?: PaginationParams) => {
    const { page, size, filters } = params || {};

    return apiClient.get('/v1/agencies/all', {
      params: {
        page,
        size,
        ...filters, 
      },
    });
  },

  /**
   * Récupère une agence par son ID
   */
  getById: (id: number) =>
    apiClient.get(`/agencies/${id}`),
  
  getCurrentAgency: () =>
    apiClient.get(`/v1/agencies/current`),
  /**
   * Vérifie un code OTP pour l'activation du compte
   */
  verifyOtp: (email: string, key: string, captchaToken: string) =>
    apiClient.put(`/v1/agencies/account/activate?email=${email}&key=${key}&captchatoken=`),
    
  /**
   * Renvoie un code OTP à l'adresse email
   */
  resendOtp: (email: string) =>
    apiClient.post(`/v1/subscribers/resend-otp?login=${email}`, { 'login': email }),
    
  /**
   * Enregistre une nouvelle agence
   */
  register: (agency: Partial<Agency>, logoFile?: File, captchaToken?: string) => {
    // Si un logo est fourni, utiliser FormData pour l'upload
    if (logoFile) {
      const formData = new FormData();
      // Ajouter toutes les données de l'agence
      Object.entries(agency).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });
      // Ajouter le fichier logo
      formData.append('logo', logoFile);
      if (captchaToken) {
        formData.append('captchatoken', captchaToken);
      }
      return apiClient.post('/v1/agencies/register', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    if (captchaToken) {
      agency.captchaToken = captchaToken; // Ajouter le token captcha aux données de l'agence
    }
    // Sans logo, envoi normal des données
    return apiClient.post('/v1/agencies/register', agency);
  },

  /**
   * Enregistre une nouvelle agence via admin
   */
  create: (agency: Partial<Agency>) => {
  
    return apiClient.post('/v1/agencies/create', agency);
  },

  /**
   * Met à jour une agence existante
   */
  update: (id: number, agency: Partial<Agency>) =>
    apiClient.put(`/v1/agencies/updateAgency/${id}`, agency),

  /**
   * Supprime une agence
   */
  delete: (id: number) =>
    apiClient.delete(`/v1/agencies/${id}`),
    
  /**
   * Active une agence
   */
  activate: (id: number) =>
    apiClient.put(`/v1/agencies/validate?idAgency=${id}`),
    
  /**
   * Suspend une agence
   */
  suspend: (id: number) =>
    apiClient.put(`/v1/agencies/suspend?idAgency=${id}`),
    
  /**
   * Réactive une agence suspendue
   */
  reinstate: (id: number) =>
    apiClient.post(`/agencies/${id}/reinstate`),
    
  /**
   * Ajoute des fonds au solde d'une agence
   */
  addFunds: (id: number, amount: number, notes?: string) =>
    apiClient.post(`/v1/agencies/add-balance`, {"agencyId": id, "amount":amount, "currency":'EUR', "notes": notes}),
    
  /**
   * Approuve une agence en attente
   */
  approveAgency: (id: number) =>
    apiClient.put(`/v1/agencies/validate?idAgency=${id}`, {'idAgency':id}),
    
  /**
   * Rejette une agence en attente
   */
  rejectAgency: (id: number) =>
    apiClient.put(`/v1/agencies/reject?idAgency=${id}`),
    
  /**
   * Obtient les agences en attente de vérification
   */
  getPendingAgencies: () =>
    apiClient.get('/v1/agencies/pending'),

  /**
   * Exporte la liste des agences au format Excel
   */
  exportToExcel: (filters?: AgencyFilter) =>
    apiClient.get('/agencies/export', { 
      params: filters,
      responseType: 'blob'
    }),
    
  /**
   * Récupère les statistiques des agences
   */
  getStats: () =>
    apiClient.get('/agencies/stats'),

  /**
   * Récupère le solde d'une agence
   */
  getBalance: (agencyId: number) =>
    apiClient.get(`/agencies/${agencyId}/balance`),

  /**
   * Débite le solde d'une agence
   */
  debitBalance: (agencyId: number, amount: number, orderId: number, description?: string) =>
    apiClient.post(`/agencies/${agencyId}/debit`, { 
      amount, 
      orderId, 
      description: description || `Paiement commande #${orderId}` 
    }),
  /**
   * Vérifie si une agence a suffisamment de solde
   */
  checkSufficientBalance: (agencyId: number,amount: number) =>
    agencyService.getCurrentAgency().then(response => { 
      const agency = response.data;
      console.log(agency);
      return {sufficient: agency.balance >= amount,
        currentBalance: agency.balance,
    }})
};